import * as Api from '@ViewModels';
import { useMemo } from 'react';
import * as AdminModels from '../../../models/AdminModels';
import { useIndustryAutomationTemplatesQuery } from './useIndustryAutomationTemplatesQuery';

export interface IndustryAutomationTemplateData {
	id: string;
	name: string;
	template: Api.IAutomationTemplate;
	industries: Api.Industry[];
	status: Api.TemplateStatus;
	systemTemplate: AdminModels.ISystemAutomationTemplate;
	creationDate: Date | string;
}

export interface IndustryAutomationTemplatesData {
	drafts: IndustryAutomationTemplateData[];
	published: IndustryAutomationTemplateData[];
	archived: IndustryAutomationTemplateData[];
	isLoading: boolean;
	isBusy: boolean;
}

function transformSystemTemplate(
	systemTemplate: AdminModels.ISystemAutomationTemplate,
	industry: Api.Industry
): IndustryAutomationTemplateData {
	const status = systemTemplate.industries[industry];
	const industries = Object.keys(systemTemplate.industries) as Api.Industry[];

	return {
		id: systemTemplate.template.id,
		name: systemTemplate.template.name,
		template: systemTemplate.template,
		industries,
		status,
		systemTemplate,
		creationDate: systemTemplate.template.creationDate,
	};
}

export function useIndustryAutomationTemplatesData({ industry }: { industry: Api.Industry }) {
	const { data: systemTemplates, isLoading } = useIndustryAutomationTemplatesQuery({
		enabled: true,
		industry,
	});

	const templatesData = useMemo<IndustryAutomationTemplatesData>(() => {
		if (!systemTemplates) {
			return {
				drafts: [],
				published: [],
				archived: [],
				isLoading,
				isBusy: isLoading,
			};
		}

		const drafts: IndustryAutomationTemplateData[] = [];
		const published: IndustryAutomationTemplateData[] = [];
		const archived: IndustryAutomationTemplateData[] = [];

		systemTemplates.forEach(systemTemplate => {
			const status = systemTemplate.industries[industry];
			if (!status) {
				return;
			}

			const templateData = transformSystemTemplate(systemTemplate, industry);

			switch (status) {
				case Api.TemplateStatus.Draft:
					drafts.push(templateData);
					break;
				case Api.TemplateStatus.Published:
					published.push(templateData);
					break;
				case Api.TemplateStatus.Archived:
					archived.push(templateData);
					break;
				default:
					break;
			}
		});

		return {
			drafts,
			published,
			archived,
			isLoading,
			isBusy: isLoading,
		};
	}, [systemTemplates, industry, isLoading]);

	return templatesData;
}
